import { isEmpty, isUndefined, merge } from 'lodash-es';

import type { AnnotationsConfigProperties } from '../../core/components/annotations/types/annotations-config-properties.js';
import { getExportableAnnotationsConfig } from '../../core/components/annotations/utils/get-exportable-annotations-config.js';
import { mergeAnnotationsConfig } from '../../core/components/annotations/utils/merge-annotations-config.js';
import type { PointsDisplay } from '../../core/components/canvas/types/canvas-shape.js';
import { isValidPointsDisplay } from '../../core/components/canvas/utils/is-valid-points-display.js';
import {
  DEFAULT_INFINITE_ZOOM,
  DEFAULT_TRUNCATION_MODE,
} from '../../core/constants.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import {
  ColorPalette,
  CustomColorPalette,
  isCustomColorPalette,
} from '../../core/types/color-palette.js';
import type { TimeGapPolicy } from '../../core/types/gap-policy.js';
import { ValueRepresentation } from '../../core/types/scales.js';
import { SeriesCurve } from '../../core/types/timeseries.js';
import { BaseToolbarProps } from '../../core/types/toolbar.js';
import { isValidCSSColor } from '../../core/utils/getValidColor.js';
import { getInteractions } from '../../core/utils/interactions.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import { isValidSeriesDisplayMode } from '../../core/utils/is-valid-series-display-mode.js';
import { isValidTooltipVariant } from '../../core/utils/is-valid-tooltip-variant.js';
import { isValidValueRepresentation } from '../../core/utils/is-valid-value-representation.js';
import {
  DEFAULT_SCALE_FALLBACK_COLOR,
  defaultColorPalette,
  defaultGapPolicy,
  defaultLegend,
  defaultPointsDisplay,
  defaultSeriesCurve,
  defaultSeriesDisplayMode,
  defaultTimeseriesChartConfig,
  defaultToolbar,
  defaultTooltipVariant,
  defaultValueRepresentation,
  defaultYAxis,
} from '../constants.js';
import type { TimeseriesAnnotationsConfigContextProps } from '../context/TimeseriesChartAnnotationsConfig.context.js';
import { TimeseriesChartLegendProps } from '../slots/Legend.js';
import { TimeseriesChartThresholdIndicatorProps } from '../slots/ThresholdIndicator.js';
import { TimeseriesChartTooltipProps } from '../slots/Tooltip.js';
import { TimeseriesChartXAxisProps } from '../slots/XAxis.js';
import { TimeseriesChartYAxisProps } from '../slots/YAxis.js';
import {
  TimeseriesChartConfigProperties,
  TimeseriesChartConfigProps,
  TimeseriesChartConfiguration,
  TimeseriesChartLegendConfig,
  TimeseriesChartSlots,
  TimeseriesChartThresholdConfig,
  TimeseriesChartThresholdsConfig,
  TimeseriesChartTooltipConfig,
  TimeseriesChartXAxisConfig,
  TimeseriesChartYAxisConfig,
} from '../types/timeseries-config.js';
import type { TimeseriesZoomDomain } from '../types/timeseries.js';
import {
  TimeseriesChartToolbarConfig,
  TimeseriesChartToolbarSlotProps,
  TimeseriesToolbarInternalProps,
} from '../types/toolbar.js';

/**
 * Returns an array with the left and right axes config in that order
 * @param yAxes - YAxis config to be sanitized
 * @returns TimeseriesChartYAxisProps[]
 */
const sanitizeYAxisConfig = (
  yAxes: TimeseriesChartYAxisProps[],
): TimeseriesChartYAxisConfig => {
  const reversedYAxes = yAxes.reverse();
  const leftAxis = reversedYAxes.find(
    (axis) => axis?.position === 'left' || axis?.position === undefined,
  );
  const rightAxis = reversedYAxes.find((axis) => axis?.position === 'right');
  const sanitizedYAxes: TimeseriesChartYAxisConfig = {};

  if (leftAxis) {
    sanitizedYAxes.left = leftAxis;
  }
  if (rightAxis) {
    sanitizedYAxes.right = rightAxis;
  }

  return sanitizedYAxes;
};

/**
 * Builds a y-axis configuration based on slots and context
 * @param slotsYAxis - y axis slots
 * @param contextYAxis - y axis context
 */
export function getYAxisConfiguration(
  slotsYAxis: TimeseriesChartYAxisProps[],
  contextYAxis: TimeseriesChartYAxisProps[] | undefined,
): TimeseriesChartYAxisConfig {
  const contextConfig =
    contextYAxis === undefined ? {} : sanitizeYAxisConfig(contextYAxis);
  const slotsConfig =
    slotsYAxis === undefined ? {} : sanitizeYAxisConfig(slotsYAxis);

  const { left, right } = merge({}, contextConfig, slotsConfig);

  if (left === undefined && right === undefined) {
    return defaultYAxis;
  }

  const result: TimeseriesChartYAxisConfig = {};

  if (left) {
    result.left = merge({}, defaultYAxis.left, left);
  }

  if (right) {
    const defaultRight = { ...defaultYAxis.left, position: 'right' };
    result.right = merge({}, defaultRight, right);
  }

  return result;
}

/**
 * Builds a legend configuration based on context and slots
 * @param contextLegend - legend context
 * @param slotsLegend - legend slots
 */
export function getLegendConfiguration(
  contextLegend: TimeseriesChartLegendProps | undefined,
  slotsLegend: TimeseriesChartLegendProps,
): TimeseriesChartLegendConfig {
  const legendConfig = merge(
    {},
    defaultLegend,
    contextLegend,
    slotsLegend,
  ) as TimeseriesChartLegendConfig;

  legendConfig.position = isValidLegendPosition(legendConfig.position)
    ? legendConfig.position
    : defaultLegend.position;

  return legendConfig;
}

/**
 * Builds a tooltip configuration based on context and slots
 * @param contextTooltip - tooltip context
 * @param slotsTooltip - tooltip slots
 */
export function getTooltipConfiguration(
  contextTooltip: TimeseriesChartTooltipProps | undefined,
  slotsTooltip: TimeseriesChartTooltipProps,
): TimeseriesChartTooltipConfig {
  const tooltip = merge(
    {},
    { variant: defaultTooltipVariant },
    contextTooltip,
    slotsTooltip,
  ) as TimeseriesChartTooltipConfig;

  tooltip.variant = isValidTooltipVariant(tooltip.variant)
    ? tooltip.variant
    : defaultTooltipVariant;

  if (tooltip.seriesDisplayMode) {
    tooltip.seriesDisplayMode = isValidSeriesDisplayMode(
      tooltip.seriesDisplayMode,
    )
      ? tooltip.seriesDisplayMode
      : defaultSeriesDisplayMode;
  } else {
    tooltip.seriesDisplayMode =
      tooltip.variant === 'shared' ? 'single-line' : 'multi-line';
  }

  return tooltip;
}

export function getAnnotationsConfiguration(
  contextAnnotations: AnnotationsConfigProperties | undefined,
  slotsAnnotations: TimeseriesAnnotationsConfigContextProps | undefined,
): TimeseriesAnnotationsConfigContextProps | undefined {
  return mergeAnnotationsConfig(contextAnnotations, slotsAnnotations);
}

/**
 * Builds an x-axis configuration based on context and slots
 * @param contextXAxis - x axis context
 * @param slotsXAxis - x axis slots
 */
export function getXAxisConfiguration(
  contextXAxis: TimeseriesChartXAxisProps | undefined,
  slotsXAxis: TimeseriesChartXAxisProps | undefined,
): TimeseriesChartXAxisConfig {
  return merge({}, contextXAxis, slotsXAxis);
}

/**
 * Builds thresholds configuration based on context and slots
 * @param contextThresholds - thresholds context
 * @param slotsThresholds - thresholds slots
 */
export function getThresholdsConfiguration(
  contextThresholds: TimeseriesChartThresholdConfig[] = [],
  slotsThresholds: TimeseriesChartThresholdIndicatorProps[] = [],
): TimeseriesChartThresholdsConfig | undefined {
  const allThresholds = contextThresholds
    .concat(slotsThresholds)
    .map((threshold) => ({
      ...threshold,
      id: crypto.randomUUID(),
      position: !threshold.position ? 'left' : threshold.position,
      color: isValidCSSColor(threshold.color)
        ? threshold.color
        : DEFAULT_SCALE_FALLBACK_COLOR,
    }));

  if (!allThresholds.length) {
    return undefined;
  }

  const thresholdConfig: TimeseriesChartThresholdsConfig = {};

  thresholdConfig.left = allThresholds.filter(
    (threshold) => threshold.position === 'left',
  );

  thresholdConfig.right = allThresholds.filter(
    (threshold) => threshold.position === 'right',
  );

  return thresholdConfig;
}

const isUndefinedOrIsEmpty = (value?: { enabled?: boolean }) => {
  return isUndefined(value) && isEmpty(value);
};

/**
 * Processes toolbar config to handle the hidden property.
 * If hidden is true, it overrides enabled and sets it to false.
 */
const processToolbarConfig = (
  config: TimeseriesChartToolbarConfig | BaseToolbarProps | undefined,
): TimeseriesChartToolbarConfig | undefined => {
  if (!config) {
    return config;
  }

  // Need to cast to access hidden property
  const { hidden, ...rest } = config as TimeseriesChartToolbarConfig & {
    hidden?: boolean;
  };

  if (hidden === true) {
    return { ...rest, enabled: false };
  }
  return config;
};

/**
 * Builds a toolbar configuration based on context and slots
 * @param contextToolbar - chart toolbar context
 * @param slotsChartToolbar - chart toolbar slots
 * @param slotDownloadCSV -
 * @param contextDownloadCSV -
 */

export function getToolbar(
  contextToolbar: TimeseriesChartToolbarConfig | undefined,
  slotsChartToolbar: TimeseriesChartToolbarSlotProps | undefined,
  slotDownloadCSV?: { enabled?: boolean },
  contextDownloadCSV?: { enabled?: boolean },
): TimeseriesToolbarInternalProps {
  const hasNoDownloadData =
    isUndefinedOrIsEmpty(slotDownloadCSV) &&
    isUndefinedOrIsEmpty(contextDownloadCSV);

  if (!contextToolbar && isEmpty(slotsChartToolbar) && hasNoDownloadData) {
    return { ...defaultToolbar };
  }

  const processedContextToolbar = processToolbarConfig(contextToolbar);
  const processedSlotsToolbar = processToolbarConfig(slotsChartToolbar);

  return merge(
    {},
    defaultToolbar,
    processedContextToolbar,
    processedSlotsToolbar,
    {
      downloadData: slotDownloadCSV ?? contextDownloadCSV,
    },
  );
}

/**
 * Builds a gapPolicy configuration based on context and props
 * @param contextGapPolicy - gap policy context
 * @param propsGapPolicy - gap policy props
 */
export function getGapPolicyConfiguration(
  contextGapPolicy?: TimeGapPolicy,
  propsGapPolicy?: TimeGapPolicy,
) {
  return propsGapPolicy ?? contextGapPolicy ?? defaultGapPolicy;
}

/**
 * Builds a pointsDisplay configuration based on context and props
 * @param contextPointsDisplay - points display context
 * @param propsPointsDisplay - points display props
 */
export function getPointsDisplayConfiguration(
  contextPointsDisplay?: PointsDisplay,
  propsPointsDisplay?: PointsDisplay,
) {
  const pointsDisplay =
    propsPointsDisplay ?? contextPointsDisplay ?? defaultPointsDisplay;

  return isValidPointsDisplay(pointsDisplay)
    ? pointsDisplay
    : defaultPointsDisplay;
}

/**
 * Builds a valueRepresentation configuration based on context and props
 * @param contextValueRepresentation - value representation context
 * @param propsValueRepresentation - value representation props
 */
export function getValueRepresentationConfiguration(
  contextValueRepresentation?: ValueRepresentation,
  propsValueRepresentation?: ValueRepresentation,
) {
  const valueRepresentation =
    propsValueRepresentation ??
    contextValueRepresentation ??
    defaultValueRepresentation;

  return isValidValueRepresentation(valueRepresentation)
    ? valueRepresentation
    : defaultValueRepresentation;
}

/**
 * Builds a ColorPalette configuration based on context and props
 * @param contextColorPalette - color palette context
 * @param propsColorPalette - color palette props
 */
export function getColorPaletteConfiguration(
  contextColorPalette?: ColorPalette | CustomColorPalette,
  propsColorPalette?: ColorPalette | CustomColorPalette,
) {
  const colorPalette =
    propsColorPalette ?? contextColorPalette ?? defaultColorPalette;

  return isCustomColorPalette(colorPalette) || isValidColorPalette(colorPalette)
    ? colorPalette
    : (defaultTimeseriesChartConfig.colorPalette ?? defaultColorPalette);
}

/**
 * Builds a curve configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getSeriesCurveConfiguration(
  contextConfig?: SeriesCurve,
  propsConfig?: SeriesCurve,
) {
  return propsConfig ?? contextConfig ?? defaultSeriesCurve;
}

/**
 * Builds infiniteZoom configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getInfiniteZoomConfiguration(
  contextConfig?: boolean,
  propsConfig?: boolean,
) {
  return propsConfig ?? contextConfig ?? DEFAULT_INFINITE_ZOOM;
}

/**
 * Builds initialZoom configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getInitialZoomConfiguration(
  contextConfig?: TimeseriesZoomDomain,
  propsConfig?: TimeseriesZoomDomain,
) {
  return propsConfig ?? contextConfig;
}

/**
 * Builds currentZoom configuration based on context and props
 * @param contextConfig -
 * @param propsConfig -
 */
export function getCurrentZoomConfiguration(
  contextConfig?: TimeseriesZoomDomain,
  propsConfig?: TimeseriesZoomDomain,
) {
  return propsConfig ?? contextConfig;
}

/**
 * Creates a timeseries Configuration merging the configuration provided in the
 * context config, slots, and defaults.
 * @param slots - time series chart slots
 * @param contextConfig - time series chart context
 * @param timeseriesChartConfigProps - time series chart props
 */
export function createTimeseriesConfiguration(
  slots: TimeseriesChartSlots,
  contextConfig: TimeseriesChartConfigProperties,
  timeseriesChartConfigProps: TimeseriesChartConfigProps,
): TimeseriesChartConfiguration {
  const yAxis = getYAxisConfiguration(slots.yAxis, contextConfig.yAxis);
  const truncationMode =
    timeseriesChartConfigProps?.truncationMode ??
    contextConfig.truncationMode ??
    DEFAULT_TRUNCATION_MODE;

  const legend = getLegendConfiguration(contextConfig.legend, slots.legend);
  const tooltip = getTooltipConfiguration(contextConfig.tooltip, slots.tooltip);
  const xAxis = getXAxisConfiguration(contextConfig.xAxis, slots.xAxis);
  const configThresholds = contextConfig.thresholdIndicators || [];
  const thresholds = getThresholdsConfiguration(
    configThresholds,
    slots.thresholds,
  );

  const toolbar = getToolbar(contextConfig.toolbar, slots.toolbar);

  const propsInteractions = {
    onZoomChange: timeseriesChartConfigProps.onZoomChange,
  };

  const hasContextInteractions =
    contextConfig && (contextConfig.pan || contextConfig.zoom);

  const chartConfigInteractions = hasContextInteractions
    ? {
        pan: { enabled: contextConfig.pan?.disabled !== true },
        zoom: { enabled: contextConfig.zoom?.disabled !== true },
        zoomX: { enabled: contextConfig.zoom?.disabled !== true },
      }
    : {};

  const interactions = getInteractions(
    contextConfig.interactions,
    slots.interactions,
    propsInteractions,
    chartConfigInteractions,
  );

  const gapPolicy = getGapPolicyConfiguration(
    contextConfig.gapPolicy,
    timeseriesChartConfigProps?.gapPolicy,
  );
  const pointsDisplay = getPointsDisplayConfiguration(
    contextConfig.pointsDisplay,
    timeseriesChartConfigProps?.pointsDisplay,
  );
  const valueRepresentation = getValueRepresentationConfiguration(
    contextConfig.valueRepresentation,
    timeseriesChartConfigProps?.valueRepresentation,
  );
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    timeseriesChartConfigProps?.colorPalette,
  );

  const curve = getSeriesCurveConfiguration(
    contextConfig.curve,
    timeseriesChartConfigProps?.curve,
  );

  const infiniteZoom = getInfiniteZoomConfiguration(
    contextConfig.infiniteZoom,
    timeseriesChartConfigProps.infiniteZoom,
  );

  const initialZoom = getInitialZoomConfiguration(
    contextConfig.initialZoom,
    timeseriesChartConfigProps.initialZoom,
  );

  const currentZoom = getCurrentZoomConfiguration(
    contextConfig.currentZoom,
    timeseriesChartConfigProps.currentZoom,
  );

  const select = timeseriesChartConfigProps.select ?? slots.select;

  const colorRules = (contextConfig.colorRules ?? [])
    .filter(isValidColorRule)
    .concat(slots.colorRules ?? []);

  return {
    gapPolicy,
    pointsDisplay,
    valueRepresentation,
    colorPalette,
    legend,
    tooltip,
    toolbar,
    xAxis,
    yAxis,
    annotations: getAnnotationsConfiguration(
      contextConfig.annotations,
      slots.annotations,
    ),
    truncationMode,
    thresholds,
    interactions,
    initialZoom,
    currentZoom,
    infiniteZoom,
    curve,
    select,
    colorRules,
  };
}

function parseYAxisConfig(yAxis: TimeseriesChartYAxisConfig) {
  const { left, right } = yAxis;
  const yAxisConfig: TimeseriesChartYAxisProps[] = [];
  if (left) {
    yAxisConfig.push(left);
  }
  if (right) {
    yAxisConfig.push(right);
  }

  return yAxisConfig;
}

function parseThresholdsConfig(
  thresholds: TimeseriesChartThresholdsConfig,
): TimeseriesChartThresholdIndicatorProps[] {
  const { left, right } = thresholds;
  const thresholdsConfig: TimeseriesChartThresholdIndicatorProps[] = [];
  if (left) {
    thresholdsConfig.push(...left);
  }
  if (right) {
    thresholdsConfig.push(...right);
  }

  return thresholdsConfig;
}

/**
 * Normalizes a final Timeseries config to look like the configuration object
 * expected by the TimeseriesChartConfig.
 * @param config - time series chart config
 */
export function normalizeConfig(
  config: TimeseriesChartConfiguration,
): TimeseriesChartConfigProperties {
  const { thresholds, annotations, ...rest } = config;
  const thresholdIndicators = thresholds
    ? parseThresholdsConfig(thresholds)
    : undefined;

  return {
    ...rest,
    yAxis: parseYAxisConfig(config.yAxis),
    thresholdIndicators,
    annotations: getExportableAnnotationsConfig(annotations),
  };
}

// Regex for validating dates like '2022-07-28T00:00:00Z' or '2022-07-28T00:00:00.000Z'
// (the latter is what JSON.stringify(new Date()) - and so getConfig() - always produces)
const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

/**
 * Function in charge of filtering those date props with a specific
 * format defined by above regex, and convert them to date.
 *
 * @param key - Property name
 * @param value - Property value
 * @returns the string date converted to javascript Date object.
 */
function reviver(key: string, value: string) {
  if (typeof value === 'string' && dateFormat.test(value)) {
    return new Date(value);
  }

  return value;
}

/**
 * Deserializes a Timeseries string config
 * @param serializedConfig - time series JSON config
 */
export function deserializeConfig(
  serializedConfig: string,
): TimeseriesChartConfigProperties {
  try {
    return merge(
      {},
      defaultTimeseriesChartConfig,
      JSON.parse(serializedConfig, reviver),
    );
  } catch {
    return defaultTimeseriesChartConfig;
  }
}
